import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Map as MapIcon, Image as ImageIcon, Sun, Info, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard } from '@/components/primitives/StatCard';
import { getPublicStatsSummary } from '@/api/public';

interface PublicStats {
  total_wildfires?: number | null;
  active_wildfires?: number | null;
  total_active?: number | null;
  total_area_hectares?: number | null;
}

interface Feature {
  to: string;
  icon: typeof Flame;
  title: string;
  description: string;
  cta: string;
}

const features: Feature[] = [
  {
    to: '/map',
    icon: MapIcon,
    title: 'Wildfire map',
    description: 'View active and recent wildfires across FPA areas on an interactive map.',
    cta: 'View map',
  },
  {
    to: '/gallery',
    icon: ImageIcon,
    title: 'Photo gallery',
    description: 'Browse community-submitted photos and videos of wildfire events.',
    cta: 'Open gallery',
  },
  {
    to: '/forecast',
    icon: Sun,
    title: 'Weather forecast',
    description: '7-day fire danger forecast including temperature, humidity and wind.',
    cta: 'Check forecast',
  },
  {
    to: '/info',
    icon: Info,
    title: 'Fire safety info',
    description: 'Fire permits, the Fire Danger Index, FPA regulations and veld fire law.',
    cta: 'Learn more',
  },
];

function formatHectares(v: number | null | undefined): string {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function HomePage() {
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await getPublicStatsSummary()) as PublicStats;
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-fire-700">
        <div className="absolute inset-0 -z-10 bg-[url('/images/hero-wildfire.jpg')] bg-cover bg-center opacity-40" />
        <div className="container relative flex flex-col items-center gap-4 px-4 py-16 text-center text-white sm:py-24">
          <Flame className="h-14 w-14 text-fire-200 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" />
          <h1 className="max-w-3xl text-3xl font-bold tracking-tight drop-shadow sm:text-5xl">
            Wildfire Reporting Portal
          </h1>
          <p className="max-w-2xl text-base text-white/90 sm:text-lg">
            Community wildfire monitoring and reporting for Fire Protection
            Association areas. Stay informed, stay safe.
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <Button asChild size="lg">
              <Link to="/map">Explore the map</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white">
              <Link to="/auth/login">Staff sign in</Link>
            </Button>
          </div>
        </div>
      </section>

      <main className="container space-y-12 py-10 sm:py-14">
        {/* Stats */}
        <section>
          <h2 className="mb-4 text-xl font-semibold tracking-tight sm:text-2xl">
            At a glance
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={Flame}
              tone="primary"
              label="Total wildfires"
              value={loading ? '' : (stats?.total_wildfires ?? '—')}
              loading={loading}
            />
            <StatCard
              icon={AlertTriangle}
              tone="destructive"
              label="Currently active"
              value={
                loading
                  ? ''
                  : (stats?.active_wildfires ?? stats?.total_active ?? '—')
              }
              loading={loading}
            />
            <StatCard
              icon={MapIcon}
              tone="secondary"
              label="Total area (ha)"
              value={loading ? '' : formatHectares(stats?.total_area_hectares)}
              loading={loading}
            />
          </div>
        </section>

        {/* Feature grid */}
        <section>
          <h2 className="mb-4 text-xl font-semibold tracking-tight sm:text-2xl">
            Explore
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.to} className="flex flex-col">
                  <CardContent className="flex flex-1 flex-col gap-3 p-5">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-base font-semibold">{feature.title}</h3>
                    <p className="flex-1 text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                    <Button asChild variant="outline" className="self-start">
                      <Link to={feature.to}>{feature.cta}</Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Footer note */}
        <section className="text-center text-sm text-muted-foreground">
          <p>
            To report a wildfire, please contact your local FPA area coordinator.
            Staff and administrators can{' '}
            <Link to="/auth/login" className="text-primary underline-offset-4 hover:underline">
              sign in here
            </Link>
            .
          </p>
        </section>
      </main>
    </>
  );
}
